import isEmpty from "lodash/isEmpty";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { MdDelete } from "react-icons/md";
import { RiInboxUnarchiveFill } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { IMAGES } from "../../assets/images";
import Header from "../../components/header";
import SearchInput from "../../components/searchInput";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { patientsQueryKey } from "../../network/globalFetches";
import { Patient } from "../../types";
import {
  ArchivePatientsListResponse,
  RemoveArchivePatientResponse,
  RestoreArchivePatientResponse,
} from "../../types/responses";
import Drawer from "../home/components/drawer";
import Skeleton from "react-loading-skeleton";
import { ICONS } from "../../assets/icons";
import toast, { Toaster } from "react-hot-toast";

const getArchivePatients = async () => {
  const { data } = await fetcher().get<ArchivePatientsListResponse>(
    API.GET.ARCHIVE_PATIENTS
  );
  return data.archivedPatients;
};

const restorePatientMutation = async (id: string) => {
  const { data } = await fetcher().post<RestoreArchivePatientResponse>(
    API.POST.RESTORE_PATIENT(id)
  );
  return data;
};

const deleteArchivePatientMutation = async (id: string) => {
  const { data } = await fetcher().delete<RemoveArchivePatientResponse>(
    API.DELETE.REMOVE_ARCHIVE_PATIENT(id)
  );
  return data;
};

const renderContent = (
  archivedPatients: Patient[],
  handleRestore: (id: string) => void,
  handleDeleteFromArchiveForever: (id: string) => void,
  isLoadingDelete: boolean,
  isLoadingData: boolean,
  isError: boolean
) => {
  if (isError) {
    return (
      <div className="flex-1 flex flex-col w-4/5 mx-auto scrollbar-hide mt-10 items-center">
        <div className="flex flex-col text-center gap-8 h-1/2 w-1/2">
          <h1 className="text-2xl">
            אופס... השרת יצא להפסקת קפה. תן לו כמה דקות ותנסה שוב! ☕
          </h1>
          <img src={ICONS.ERROR_NOT_FOUND} />
        </div>
      </div>
    );
  }

  if (isLoadingData) {
    return (
      <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto scrollbar-hide mt-10">
        <div className="text-center py-4">
          <Skeleton count={5} height={65} />
        </div>
      </div>
    );
  }

  if (isEmpty(archivedPatients)) {
    return (
      <div className="flex flex-col text-center items-center gap-10">
        <h2 className="font-bold text-xl">הארכיון ריק</h2>
        <div className="w-1/4">
          <img
            className="w-full"
            src={IMAGES.ARCHIVE_NOT_FOUND}
            alt="הארכיון ריק"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto flex flex-col w-4/5 mx-auto">
      <div className="text-center py-4">
        <AnimatePresence>
          {archivedPatients.map(({ _id, name, defaultImg }, index) => {
            return (
              <motion.div
                key={_id}
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 20 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                layout
                className={`flex justify-between hover:bg-blue-200 hover:cursor-pointer items-center mb-2 p-2 odd:bg-gray-100 border rounded ${
                  index === archivedPatients.length - 1 ? "mb-14" : ""
                }`}>
                {isLoadingDelete && (
                  <div className="absolute inset-0 bg-gray-100 animate-pulse z-10 rounded">
                    <div className="h-full w-full bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 animate-shimmer"></div>
                  </div>
                )}
                <div className="flex gap-4 text-right items-center">
                  <div className="rounded-full w-14">
                    <img
                      className="w-full rounded-full"
                      src={defaultImg}
                      alt="profileImage"
                    />
                  </div>
                  <p>{name}</p>
                </div>
                <div className="flex items-center gap-14">
                  <button
                    className="text-center items-center flex flex-col border rounded px-2 hover:scale-110 hover:border-[#C64192]"
                    onClick={() => {
                      Swal.fire({
                        icon: "warning",
                        title: `${name} ימחק לתמיד`,
                        showConfirmButton: true,
                        confirmButtonText: "מחק",
                        confirmButtonColor: "red",
                        showCancelButton: true,
                        cancelButtonText: "ביטול",
                      }).then(({ isConfirmed }) => {
                        if (isConfirmed) {
                          handleDeleteFromArchiveForever(_id);
                        }
                      });
                    }}>
                    <span>מחק</span>
                    <MdDelete size={22} className="hover:scale-125" />
                  </button>

                  <button
                    className="text-center items-center flex flex-col border rounded px-2 hover:scale-110 hover:border-[#C64192]"
                    onClick={() => {
                      handleRestore(_id);
                    }}>
                    <span>שחזר</span>
                    <RiInboxUnarchiveFill
                      size={22}
                      className="hover:scale-125"
                    />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

const ArchivePage = () => {
  const [archivedPatients, setArchivedPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["archive"],
    queryFn: getArchivePatients,
  });

  const { mutate: deleteArchive, isPending: isLoadingDeleteArchive } =
    useMutation<RemoveArchivePatientResponse, Error, string>({
      mutationFn: deleteArchivePatientMutation,
      onSuccess: ({ archivePatientId }) => {
        setArchivedPatients((prev) =>
          prev.filter(
            (archivePatient) => archivePatient._id !== archivePatientId
          )
        );
        toast.success("הדייר נמחק בהצלחה");
      },
      onError: () => {
        toast.error("תקלת מערכת - הדייר לא נמחק. נסה שנית מאוחר יותר");
      },
    });

  const { mutate: restoreArchive, isPending: isLoadingRestore } = useMutation<
    RestoreArchivePatientResponse,
    Error,
    string
  >({
    mutationFn: restorePatientMutation,
    onSuccess: ({ patient }) => {
      queryClient.setQueryData<Patient[]>(patientsQueryKey, (oldData) => {
        if (!oldData) return [patient];
        oldData.unshift(patient);
        return oldData;
      });

      setArchivedPatients((prev) =>
        prev.filter((archivePatient) => archivePatient._id !== patient._id)
      );

      toast.success(`הדייר ${patient.name} שוחזר בהצלחה`);
    },
    onError: () => {
      toast.error("שגיאת מערכת - הדייר ברח לנו. נסה שוב עוד מעט!");
    },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    let filtered = data;

    if (searchTerm) {
      filtered = filtered.filter((archivedPatient) =>
        archivedPatient.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setArchivedPatients(filtered);
  }, [searchTerm, data]);

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  return (
    <div className="container mx-auto h-screen flex flex-col">
      <Header>
        <Header.Time />
        <Header.Title title="ארכיון דיירים" />
        <Header.Menu toggleDrawer={toggleDrawer} />
      </Header>

      {!isError && (
        <div className="w-1/3 mx-auto mt-6">
          <SearchInput placeholder="חפש דייר" onSearch={setSearchTerm} />
        </div>
      )}

      <div className="mt-10">
        {!isEmpty(archivedPatients) && !isError && (
          <p className="mb-2 w-4/5 mx-auto">
            מספר דיירים בארכיון: {archivedPatients.length}
          </p>
        )}
        {renderContent(
          archivedPatients,
          restoreArchive,
          deleteArchive,
          isLoadingDeleteArchive || isLoadingRestore,
          isLoading,
          isError
        )}
      </div>
      {isDrawerOpen && <Drawer toggleDrawer={toggleDrawer} />}
      <Toaster position="bottom-center" />
    </div>
  );
};
export default ArchivePage;
